/**
 * Checked-in Flow IR samples. These are BYTE-FAITHFUL copies of real, config-only fixtures from the
 * engine repo (no PII) — the canvas renders the same IR the compiler consumes. In PS-2 this registry
 * is replaced by `GET /api/v1/flows` (Track A product API).
 *
 * Provenance (see samples/README.md):
 *  - pkyc-review.v1.yaml ....... lifecycle-clocks/.../flows/lifecycle/pkyc-review.v1.yaml (PRD Flow-6/T6)
 *  - corp-ind-onboarding-*.yaml  flow-ir/src/test/resources/fixtures/corpus/ (IN-68 corpus translation)
 */
import pkycRaw from './pkyc-review.v1.yaml?raw';
import corpJourneyRaw from './corp-ind-onboarding-journey.v1.yaml?raw';
import corpDirectorsRaw from './corp-ind-onboarding-directors.v1.yaml?raw';
import { parseFlowYaml } from '../parse';
import { isVariant, type FlowDefinition } from '../types';

export interface SampleFlow {
  /** Route key (== flow id). */
  key: string;
  raw: string;
  def: FlowDefinition;
  provenance: string;
}

export interface FlowSummary {
  id: string;
  name: string;
  description?: string;
  version: number;
  stages: string[];
  jurisdictions: string[];
  labels: string[];
  nodeCount: number;
  edgeCount: number;
  variant: boolean;
  provenance: string;
}

const RAW: Array<{ raw: string; provenance: string }> = [
  {
    raw: pkycRaw,
    provenance: 'lifecycle-clocks · pKYC materiality review (PRD Flow-6 / template T6)',
  },
  {
    raw: corpJourneyRaw,
    provenance: 'flow-ir corpus fixture · IN-68 Corporate & Individual Onboarding (archetype A2)',
  },
  {
    raw: corpDirectorsRaw,
    provenance: 'flow-ir corpus fixture · IN-68 Directors per-role sub-flow (archetype A3)',
  },
];

export const SAMPLE_FLOWS: SampleFlow[] = RAW.map(({ raw, provenance }) => {
  const def = parseFlowYaml(raw);
  return { key: def.id, raw, def, provenance };
});

export function getSample(key: string): SampleFlow | undefined {
  return SAMPLE_FLOWS.find((s) => s.key === key);
}

export function summarize(s: SampleFlow): FlowSummary {
  const { def } = s;
  return {
    id: def.id,
    name: def.metadata.name,
    description: def.metadata.description,
    version: def.version,
    stages: def.metadata.stages ?? [],
    jurisdictions: def.metadata.jurisdictions ?? [],
    labels: def.metadata.labels ?? [],
    nodeCount: def.nodes?.length ?? 0,
    edgeCount: def.edges?.length ?? 0,
    variant: isVariant(def),
    provenance: s.provenance,
  };
}

export const FLOW_SUMMARIES: FlowSummary[] = SAMPLE_FLOWS.map(summarize);
